import { useTranslation } from "react-i18next";
import { translate as tr } from "../../i18n";
import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../common/sidebar';
import '../../assets/css/dashboard.css';
import '../../assets/css/help.css';

/* ── Icons ──────────────────────────────────────────────────── */
const Icon = ({ d, size = 16, ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...rest}>
    {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
  </svg>
);

const ICONS = {
  search: ['M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z'],
  timer:  ['M12 2a10 10 0 1 0 0 20A10 10 0 0 0 12 2z', 'M12 6v6l4 2'],
  project:['M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z'],
  team:   ['M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2', 'M23 21v-2a4 4 0 0 0-3-3.87', 'M16 3.13a4 4 0 0 1 0 7.75', 'M9 7m-4 0a4 4 0 1 0 8 0a4 4 0 1 0-8 0'],
  report: ['M18 20V10', 'M12 20V4', 'M6 20v-6'],
  hr:     ['M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2', 'M12 3m-4 0a4 4 0 1 0 8 0a4 4 0 1 0-8 0'],
  invoice:['M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z', 'M14 2v6h6', 'M16 13H8', 'M16 17H8', 'M10 9H8'],
  settings:['M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z', 'M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z'],
  keyboard:['M20 5H4a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2z', 'M8 10h.01', 'M12 10h.01', 'M16 10h.01', 'M8 14h8', 'M10 14v0'],
  mail:    ['M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z', 'M22 6l-10 7L2 6'],
  start:   ['M5 3l14 9-14 9V3z'],
  tag:     ['M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z', 'M7 7h.01'],
  chevron: 'M6 9l6 6 6-6',
  back:    'M19 12H5M12 5l-7 7 7 7',
};

/* ── FAQ data ────────────────────────────────────────────────── */
const SECTIONS = [
  {
    id: 'getting-started',
    get title() { return tr("Getting Started"); },
    iconKey: 'start',
    faqs: [
      {
        get q() { return tr("helpFaq1Question"); },
        get a() { return tr("helpFaq1Answer"); }
      },
      {
        get q() { return tr("helpFaq2Question"); },
        get a() { return tr("helpFaq2Answer"); }
      },
      {
        get q() { return tr("helpFaq3Question"); },
        get a() { return tr("helpFaq3Answer"); }
      },
      {
        get q() { return tr("helpFaq4Question"); },
        get a() { return tr("helpFaq4Answer"); }
      },
    ],
  },
  {
    id: 'timer',
    get title() { return tr("Timer & Time Tracking"); },
    iconKey: 'timer',
    faqs: [
      {
        get q() { return tr("helpFaq5Question"); },
        get a() { return tr("helpFaq5Answer"); }
      },
      {
        get q() { return tr("helpFaq6Question"); },
        get a() { return tr("helpFaq6Answer"); }
      },
      {
        get q() { return tr("helpFaq7Question"); },
        get a() { return tr("helpFaq7Answer"); }
      },
      {
        get q() { return tr("helpFaq8Question"); },
        get a() { return tr("helpFaq8Answer"); }
      },
      {
        get q() { return tr("helpFaq9Question"); },
        get a() { return tr("helpFaq9Answer"); }
      },
      {
        get q() { return tr("helpFaq10Question"); },
        get a() { return tr("helpFaq10Answer"); }
      },
    ],
  },
  {
    id: 'projects',
    get title() { return tr("Projects & Tags"); },
    iconKey: 'project',
    faqs: [
      {
        get q() { return tr("helpFaq11Question"); },
        get a() { return tr("helpFaq11Answer"); }
      },
      {
        get q() { return tr("helpFaq12Question"); },
        get a() { return tr("helpFaq12Answer"); }
      },
      {
        get q() { return tr("helpFaq13Question"); },
        get a() { return tr("helpFaq13Answer"); }
      },
      {
        get q() { return tr("helpFaq14Question"); },
        get a() { return tr("helpFaq14Answer"); }
      },
    ],
  },
  {
    id: 'team',
    get title() { return tr("Team & Workspace"); },
    iconKey: 'team',
    faqs: [
      {
        get q() { return tr("helpFaq15Question"); },
        get a() { return tr("helpFaq15Answer"); }
      },
      {
        get q() { return tr("helpFaq16Question"); },
        get a() { return tr("helpFaq16Answer"); }
      },
      {
        get q() { return tr("helpFaq17Question"); },
        get a() { return tr("helpFaq17Answer"); }
      },
    ],
  },
  {
    id: 'reports',
    get title() { return tr("Reports & Insights"); },
    iconKey: 'report',
    faqs: [
      {
        get q() { return tr("helpFaq18Question"); },
        get a() { return tr("helpFaq18Answer"); }
      },
      {
        get q() { return tr("helpFaq19Question"); },
        get a() { return tr("helpFaq19Answer"); }
      },
      {
        get q() { return tr("helpFaq20Question"); },
        get a() { return tr("helpFaq20Answer"); }
      },
      {
        get q() { return tr("helpFaq21Question"); },
        get a() { return tr("helpFaq21Answer"); }
      },
    ],
  },
  {
    id: 'hr',
    get title() { return tr("HR Module (Attendance & Leave)"); },
    iconKey: 'hr',
    faqs: [
      {
        get q() { return tr("helpFaq22Question"); },
        get a() { return tr("helpFaq22Answer"); }
      },
      {
        get q() { return tr("helpFaq23Question"); },
        get a() { return tr("helpFaq23Answer"); }
      },
      {
        get q() { return tr("helpFaq24Question"); },
        get a() { return tr("helpFaq24Answer"); }
      },
      {
        get q() { return tr("helpFaq25Question"); },
        get a() { return tr("helpFaq25Answer"); }
      },
      {
        get q() { return tr("helpFaq26Question"); },
        get a() { return tr("helpFaq26Answer"); }
      },
      {
        get q() { return tr("helpFaq27Question"); },
        get a() { return tr("helpFaq27Answer"); }
      },
    ],
  },
  {
    id: 'invoices',
    get title() { return tr("Invoices & Clients"); },
    iconKey: 'invoice',
    faqs: [
      {
        get q() { return tr("helpFaq28Question"); },
        get a() { return tr("helpFaq28Answer"); }
      },
      {
        get q() { return tr("helpFaq29Question"); },
        get a() { return tr("helpFaq29Answer"); }
      },
      {
        get q() { return tr("helpFaq30Question"); },
        get a() { return tr("helpFaq30Answer"); }
      },
      {
        get q() { return tr("helpFaq31Question"); },
        get a() { return tr("helpFaq31Answer"); }
      },
    ],
  },
  {
    id: 'account',
    get title() { return tr("Account & Settings"); },
    iconKey: 'settings',
    faqs: [
      {
        get q() { return tr("helpFaq32Question"); },
        get a() { return tr("helpFaq32Answer"); }
      },
      {
        get q() { return tr("helpFaq33Question"); },
        get a() { return tr("helpFaq33Answer"); }
      },
      {
        get q() { return tr("helpFaq34Question"); },
        get a() { return tr("helpFaq34Answer"); }
      },
      {
        get q() { return tr("helpFaq35Question"); },
        get a() { return tr("helpFaq35Answer"); }
      },
    ],
  },
  {
    id: 'shortcuts',
    get title() { return tr("Keyboard Shortcuts"); },
    iconKey: 'keyboard',
    faqs: [
      {
        get q() { return tr("helpFaq36Question"); },
        get a() { return tr("helpFaq36Answer"); }
      },
    ],
  },
];

const QUICK_LINKS = [
  { get label() { return tr("Start Timer"); }, iconKey: 'timer', href: '/dashboard' },
  { get label() { return tr("Projects"); }, iconKey: 'project', href: '/projects' },
  { label: 'Team', iconKey: 'team', href: '/team' },
  { get label() { return tr("Reports"); }, iconKey: 'report', href: '/report' },
  { get label() { return tr("Attendance"); }, iconKey: 'hr', href: '/attendance' },
  { get label() { return tr("Leave"); }, iconKey: 'hr', href: '/leave' },
  { get label() { return tr("Invoices"); }, iconKey: 'invoice', href: '/invoices' },
  { get label() { return tr("Settings"); }, iconKey: 'settings', href: '/settings' },
];

const ALLOWED_FAQ_TAGS = new Set(['p', 'strong', 'ul', 'ol', 'li', 'table', 'tr', 'td', 'th', 'kbd', 'span', 'a', 'br', 'em', 'i', 'b', 'code']);

const parseInlineStyle = (styleText) => {
  if (!styleText) return undefined;
  return styleText.split(';').reduce((acc, declaration) => {
    const [rawKey, rawValue] = declaration.split(':');
    if (!rawKey || !rawValue) return acc;
    const key = rawKey.trim().replace(/-([a-z])/g, (_, letter) => letter.toUpperCase());
    const value = rawValue.trim();
    if (key) acc[key] = value;
    return acc;
  }, {});
};

const renderFaqNodes = (nodes, path = 'faq') =>
  Array.from(nodes).map((node, index) => {
    const key = `${path}-${index}`;
    if (node.nodeType === Node.TEXT_NODE) {
      return node.textContent;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) {
      return null;
    }

    const tagName = node.tagName.toLowerCase();
    const children = renderFaqNodes(node.childNodes, key);

    if (!ALLOWED_FAQ_TAGS.has(tagName)) {
      return React.createElement(React.Fragment, { key }, children);
    }

    if (tagName === 'br') {
      return React.createElement('br', { key });
    }

    const props = { key };
    if (node.className) props.className = node.className;
    if (node.getAttribute('style')) props.style = parseInlineStyle(node.getAttribute('style'));

    if (tagName === 'a') {
      const href = node.getAttribute('href') || '';
      if (/^(https?:|mailto:|\/|#)/i.test(href)) {
        props.href = href;
        if (node.getAttribute('target')) props.target = node.getAttribute('target');
        if (node.getAttribute('rel')) props.rel = node.getAttribute('rel');
      }
      return React.createElement('a', props, children);
    }

    return React.createElement(tagName, props, children);
  });

const renderFaqContent = (html) => {
  if (typeof window === 'undefined' || !html) {
    return html;
  }

  const doc = new DOMParser().parseFromString(html, 'text/html');
  return <>{renderFaqNodes(doc.body.childNodes)}</>;
};

/* ── Component ───────────────────────────────────────────────── */
export default function HelpPage() {
  const { i18n: helpI18n } = useTranslation();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [openItems, setOpenItems] = useState({});
  const [activeSection, setActiveSection] = useState('getting-started');
  const sectionRefs = useRef({});

  const toggleItem = (sectionId, idx) => {
    const key = `${sectionId}-${idx}`;
    setOpenItems(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const isOpen = (sectionId, idx) => !!openItems[`${sectionId}-${idx}`];

  const q = query.toLowerCase().trim();

  const filteredSections = useMemo(() => {
    if (!q) return SECTIONS;
    return SECTIONS.map(section => ({
      ...section,
      faqs: section.faqs.filter(
        faq =>
          faq.q.toLowerCase().includes(q) ||
          faq.a.toLowerCase().includes(q)
      ),
    })).filter(s => s.faqs.length > 0);
  }, [q, helpI18n.resolvedLanguage]);

  // Auto-expand all on search
  useEffect(() => {
    if (q) {
      const opened = {};
      filteredSections.forEach(s =>
        s.faqs.forEach((_, i) => { opened[`${s.id}-${i}`] = true; })
      );
      setOpenItems(opened);
    }
  }, [q, helpI18n.resolvedLanguage]); // eslint-disable-line

  // Scroll spy
  useEffect(() => {
    const obs = new IntersectionObserver(
      entries => {
        entries.forEach(e => {
          if (e.isIntersecting) setActiveSection(e.target.dataset.sectionId);
        });
      },
      { threshold: 0.3 }
    );
    Object.values(sectionRefs.current).forEach(el => el && obs.observe(el));
    return () => obs.disconnect();
  }, [filteredSections]);

  const scrollTo = (id) => {
    const el = sectionRefs.current[id];
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <>
      <Sidebar />
      <div className="content-wrap" id="wrapper">
        <div className="main help-page">

          {/* Back */}
          <button
            className="cs-back-btn"
            onClick={() => navigate(-1)}
            style={{ marginBottom: 16 }}
          >
            <Icon d={ICONS.back} size={15} />{tr("Back")} </button>

          {/* Hero / Search */}
          <div className="help-hero">
            <h1>{tr("Help Center")}</h1>
            <p>{tr("Search our docs, browse FAQs, or contact support — we're here to help.")}</p>
            <div className="help-search-wrap">
              <Icon d={ICONS.search} size={16} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#999', pointerEvents: 'none' }} />
              <input
                className="help-search"
                type="text"
                placeholder={tr("Search for answers… (e.g. 'start timer', 'leave approval')")}
                value={query}
                onChange={e => setQuery(e.target.value)}
                autoFocus
              />
            </div>
          </div>

          {/* Quick links */}
          {!q && (
            <div className="help-quick-grid">
              {QUICK_LINKS.map(lnk => (
                <a key={lnk.href} href={lnk.href} className="help-quick-card"
                  onClick={e => { e.preventDefault(); navigate(lnk.href); }}>
                  <div className="help-quick-icon">
                    <Icon d={ICONS[lnk.iconKey]} size={18} />
                  </div>
                  <span className="help-quick-label">{lnk.label}</span>
                </a>
              ))}
            </div>
          )}

          {/* Body: TOC + sections */}
          <div className="help-body">

            {/* Table of contents (hidden on search) */}
            {!q && (
              <nav className="help-toc">
                <div className="help-toc-title">{tr("Contents")}</div>
                {SECTIONS.map(s => (
                  <div
                    key={s.id}
                    className={`help-toc-item ${activeSection === s.id ? 'active' : ''}`}
                    onClick={() => scrollTo(s.id)}
                  >
                    <Icon d={ICONS[s.iconKey]} size={14} />
                    {s.title}
                  </div>
                ))}
              </nav>
            )}

            {/* FAQ sections */}
            <div>
              {filteredSections.length === 0 ? (
                <div className="help-no-results">
                  <Icon d={ICONS.search} size={40} />
                  <p>{tr("No results for")} <strong>"{query}"</strong>{tr(". Try different keywords or")} <a href="mailto:support@minutezilla.com">{tr("contact support")}</a>.</p>
                </div>
              ) : (
                filteredSections.map(section => (
                  <div
                    key={section.id}
                    className="help-section"
                    data-section-id={section.id}
                    ref={el => { sectionRefs.current[section.id] = el; }}
                  >
                    <div className="help-section-header">
                      <div className="help-section-icon">
                        <Icon d={ICONS[section.iconKey]} size={18} />
                      </div>
                      <h2 className="help-section-title">{section.title}</h2>
                      <span className="help-section-count">{section.faqs.length}</span>
                    </div>

                    {section.faqs.map((faq, idx) => (
                      <div key={idx} className="help-faq-item">
                        <button
                          className={`help-faq-q ${isOpen(section.id, idx) ? 'open' : ''}`}
                          onClick={() => toggleItem(section.id, idx)}
                        >
                          <span>{faq.q}</span>
                          <svg className="help-faq-chevron" width="16" height="16" viewBox="0 0 24 24"
                            fill="none" stroke="currentColor" strokeWidth="2"
                            strokeLinecap="round" strokeLinejoin="round">
                            <path d={ICONS.chevron} />
                          </svg>
                        </button>
                        <div className={`help-faq-a ${isOpen(section.id, idx) ? 'open' : ''}`}>
                          <div className="help-faq-a-inner">
                            {renderFaqContent(faq.a)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ))
              )}

              {/* Contact support */}
              <div className="help-contact-card">
                <div className="help-contact-icon">
                  <Icon d={ICONS.mail} size={24} />
                </div>
                <div className="help-contact-body">
                  <h3>{tr("Still need help?")}</h3>
                  <p>{tr("Our support team typically responds within one business day.")}</p>
                  <a href="mailto:support@minutezilla.com">{tr("Email support@minutezilla.com")}</a>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </>
  );
}
